import { useState } from "react";
import api from "@/lib/api";
import { Panel, EmptyState, StatusBadge } from "@/components/admin/shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LEAD_STATUSES } from "@/lib/constants";
import { Check, CalendarPlus, StickyNote } from "lucide-react";
import { toast } from "sonner";

const fmt = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export const ProfileNotes = ({ visitorId, visitorNotes = [], leads = [], onChange }) => {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const all = [
    ...visitorNotes.map((n) => ({ ...n, src: "Profile" })),
    ...leads.flatMap((l) => (l.notes || []).map((n) => ({ ...n, src: "Lead" }))),
  ].sort((a, b) => (b.at || "").localeCompare(a.at || ""));

  const save = async () => {
    setSaving(true);
    try { await api.post(`/visitors/${visitorId}/notes`, { text }); setText(""); toast.success("Note saved"); onChange(); }
    catch { toast.error("Could not save note"); }
    setSaving(false);
  };

  return (
    <Panel title={`Notes (${all.length})`}>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a note about this client…" rows={3} data-testid="profile-note-textarea" />
      <div className="flex justify-end mt-2">
        <Button onClick={save} disabled={!text.trim() || saving} className="btn-orange h-9 rounded-xl" data-testid="profile-note-save-btn"><StickyNote size={15} /> Save Note</Button>
      </div>
      <div className="mt-4 space-y-2 max-h-72 overflow-y-auto" data-testid="profile-notes-list">
        {all.length ? all.map((n, i) => (
          <div key={n.note_id || i} className="p-3 rounded-xl bg-slate-50 text-sm" data-testid="profile-note-item">
            <p className="text-slate-700 whitespace-pre-wrap">{n.text}</p>
            <p className="text-[11px] text-slate-400 mt-1">{n.by} · {fmt(n.at)} · {n.src}</p>
          </div>
        )) : <EmptyState text="No notes yet" />}
      </div>
    </Panel>
  );
};

export const ProfileFollowups = ({ visitorId, followups = [], team = [], onChange }) => {
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [assignee, setAssignee] = useState("");
  const teamName = (id) => team.find((t) => t.member_id === id)?.name || "Unassigned";
  const sorted = [...followups].sort((a, b) => (a.followup_date || "").localeCompare(b.followup_date || ""));

  const schedule = async () => {
    try {
      await api.post(`/visitors/${visitorId}/followup`, { followup_date: new Date(date).toISOString(), notes, assigned_to: assignee || null });
      toast.success("Follow-up scheduled"); setDate(""); setNotes(""); setAssignee(""); onChange();
    } catch { toast.error("Could not schedule follow-up"); }
  };
  const complete = async (id) => { await api.patch(`/followups/${id}`, { status: "Completed" }); toast.success("Marked complete"); onChange(); };

  return (
    <Panel title={`Follow-ups (${followups.length})`}>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} data-testid="profile-followup-date-input" />
        <Select value={assignee} onValueChange={setAssignee}>
          <SelectTrigger data-testid="profile-followup-assign-select"><SelectValue placeholder="Assign to…" /></SelectTrigger>
          <SelectContent>{team.map((t) => <SelectItem key={t.member_id} value={t.member_id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What should be discussed?" rows={2} className="mt-2" data-testid="profile-followup-notes-textarea" />
      <div className="flex justify-end mt-2">
        <Button onClick={schedule} disabled={!date} className="btn-orange h-9 rounded-xl" data-testid="profile-followup-save-btn"><CalendarPlus size={15} /> Schedule</Button>
      </div>
      <div className="mt-4 space-y-2 max-h-72 overflow-y-auto">
        {sorted.length ? sorted.map((f) => (
          <div key={f.followup_id} className="p-3 rounded-xl bg-slate-50 text-sm flex items-start justify-between gap-3" data-testid="profile-followup-item">
            <div>
              <p className="font-semibold text-[#081226]">{fmt(f.followup_date)}</p>
              <p className="text-xs text-slate-500">{teamName(f.assigned_to)}{f.notes ? ` · ${f.notes}` : ""}</p>
            </div>
            {f.status === "Completed"
              ? <span className="text-[11px] font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">Done</span>
              : <button onClick={() => complete(f.followup_id)} className="p-1.5 rounded-lg bg-white border border-slate-200 text-emerald-600" title="Mark complete" data-testid="profile-followup-complete-btn"><Check size={15} /></button>}
          </div>
        )) : <EmptyState text="No follow-ups scheduled" />}
      </div>
    </Panel>
  );
};

export const ProfileLeads = ({ leads = [], onStage }) => (
  <Panel title={`Leads (${leads.length})`}>
    {leads.length ? leads.map((l) => (
      <div key={l.lead_id} className="p-3 rounded-xl bg-slate-50 mb-2 text-sm flex items-center justify-between gap-2">
        <div className="min-w-0"><p className="font-mono-num text-xs text-[#B8860B] font-bold">{l.lead_id}</p><p className="text-slate-500 text-xs truncate">{l.interests?.join(", ") || l.purpose}</p></div>
        <Select value={l.status} onValueChange={(v) => onStage(l, v)}>
          <SelectTrigger className="h-8 w-[140px] rounded-lg text-xs bg-white" data-testid="profile-lead-stage-select"><SelectValue /></SelectTrigger>
          <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    )) : <EmptyState text="No leads" />}
  </Panel>
);

export { StatusBadge };
